/**
 * /dashboard/accounting/money — Money hub (G_ACC6c).
 *
 * Wallets master + recent receipts/payments at-a-glance. The "New
 * wallet" CTA opens an inline dialog (master CRUD); "New receipt" and
 * "New payment" route to the per-doc create pages.
 *
 * Recent panels show 5 rows each, capped via ReceiptList/PaymentList's
 * `limit` prop, with a "View all" link to the dedicated list page.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/money/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowRight, Plus } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { AccountingV1ScopeModal } from "@/components/accounting/v1-scope-modal";
import { PaymentAccountList } from "@/components/accounting/payment-account-list";
import { PaymentAccountForm } from "@/components/accounting/payment-account-form";
import { ReceiptList } from "@/components/accounting/receipt-list";
import { PaymentList } from "@/components/accounting/payment-list";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { PaymentAccount } from "@/types/accounting";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/money/")({
  component: MoneyHubRoute,
});

function MoneyHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState<PaymentAccount | undefined>(
    undefined,
  );

  function openCreate() {
    setEditingWallet(undefined);
    setWalletDialogOpen(true);
  }

  function openEdit(pa: PaymentAccount) {
    setEditingWallet(pa);
    setWalletDialogOpen(true);
  }

  return (
    <AccountingModuleShell
      title="Money"
      description="Wallets, customer receipts and vendor payments — books-of-account compliant."
      actions={
        <>
          <Button
            variant="outline"
            onClick={openCreate}
            className="gap-1.5"
            data-testid="money-hub-new-wallet"
          >
            <Plus aria-hidden className="h-4 w-4" />
            New wallet
          </Button>
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/money/receipts/new" params={{ tenantSlug }}
              data-testid="money-hub-new-receipt"
            >
              <Plus aria-hidden className="h-4 w-4" />
              New receipt
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/money/payments/new" params={{ tenantSlug }}
              data-testid="money-hub-new-payment"
            >
              <Plus aria-hidden className="h-4 w-4" />
              New payment
            </Link>
          </Button>
        </>
      }
    >
      <AccountingV1ScopeModal />

      <div className="space-y-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold tracking-tight text-gray-900">
              Wallets
            </h2>
          </div>
          <PaymentAccountList onEdit={openEdit} />
        </section>

        <section className="space-y-3">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Recent receipts</CardTitle>
                <Link
                  to="/app/$tenantSlug/accounting/money/receipts" params={{ tenantSlug }}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  View all
                  <ArrowRight aria-hidden className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <ReceiptList showFilters={false} limit={5} />
            </CardContent>
          </Card>
        </section>

        <section className="space-y-3">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Recent payments</CardTitle>
                <Link
                  to="/app/$tenantSlug/accounting/money/payments" params={{ tenantSlug }}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  View all
                  <ArrowRight aria-hidden className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <PaymentList showFilters={false} limit={5} />
            </CardContent>
          </Card>
        </section>
      </div>

      <PaymentAccountForm
        open={walletDialogOpen}
        onOpenChange={setWalletDialogOpen}
        account={editingWallet}
      />
    </AccountingModuleShell>
  );
}
